import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SubmitIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchAppointmentById } from '../../store/slices/appointmentSlice';

/**
 * Visit Report / Medical Examination Form
 * Complete form for doctors to fill during patient visits
 *
 * @author Civilhealth Development Team
 */

interface VitalSigns {
  bloodPressureSystolic: number | null;
  bloodPressureDiastolic: number | null;
  pulseRate: number | null;
  temperature: number | null;
  respiratoryRate: number | null;
  oxygenSaturation: number | null;
  weight: number | null;
  height: number | null;
  bmi: number | null;
  bloodSugarFasting: number | null;
  bloodSugarPostMeal: number | null;
  bloodSugarRandom: number | null;
}

interface Symptoms {
  fever: boolean;
  cough: boolean;
  bodyPain: boolean;
  headache: boolean;
  cold: boolean;
  breathlessness: boolean;
  chestPain: boolean;
  dizziness: boolean;
  fatigue: boolean;
  nausea: boolean;
  other: string;
}

interface Diagnosis {
  provisional: string;
  final: string;
  icdCode: string;
  clinicalFindings: string;
}

interface Prescription {
  medicineName: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
}

interface FollowUp {
  required: boolean;
  date: string;
  priority: string;
  instructions: string;
}

const steps = ['Patient Info', 'Vitals', 'Symptoms', 'Diagnosis', 'Prescription', 'Follow-up'];

const CreateVisitReportPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { currentAppointment, loading: appointmentLoading } = useSelector(
    (state: RootState) => state.appointment
  );

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [vitalSigns, setVitalSigns] = useState<VitalSigns>({
    bloodPressureSystolic: null,
    bloodPressureDiastolic: null,
    pulseRate: null,
    temperature: null,
    respiratoryRate: null,
    oxygenSaturation: null,
    weight: null,
    height: null,
    bmi: null,
    bloodSugarFasting: null,
    bloodSugarPostMeal: null,
    bloodSugarRandom: null,
  });

  const [symptoms, setSymptoms] = useState<Symptoms>({
    fever: false,
    cough: false,
    bodyPain: false,
    headache: false,
    cold: false,
    breathlessness: false,
    chestPain: false,
    dizziness: false,
    fatigue: false,
    nausea: false,
    other: '',
  });

  const [chiefComplaints, setChiefComplaints] = useState('');
  const [symptomNotes, setSymptomNotes] = useState('');

  const [diagnosis, setDiagnosis] = useState<Diagnosis>({
    provisional: '',
    final: '',
    icdCode: '',
    clinicalFindings: '',
  });

  const [prescriptions, setPrescriptions] = useState<Prescription[]>([
    { medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' },
  ]);

  const [followUp, setFollowUp] = useState<FollowUp>({
    required: false,
    date: '',
    priority: 'NORMAL',
    instructions: '',
  });

  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (appointmentId) {
      dispatch(fetchAppointmentById(Number(appointmentId)));
    }
  }, [appointmentId, dispatch]);

  useEffect(() => {
    if (vitalSigns.weight && vitalSigns.height && vitalSigns.height > 0) {
      const heightInMeters = vitalSigns.height / 100;
      const bmi = Number((vitalSigns.weight / (heightInMeters * heightInMeters)).toFixed(1));
      setVitalSigns((prev) => ({ ...prev, bmi }));
    }
  }, [vitalSigns.weight, vitalSigns.height]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      // API call to save draft
      // await dispatch(saveVisitReportDraft({ ... }));
      // Show success message
    } catch (err: any) {
      setError(err.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      // API call to submit report
      // await dispatch(submitVisitReport({ ... }));
      navigate('/doctor/workflow');
    } catch (err: any) {
      setError(err.message || 'Failed to submit report');
    } finally {
      setSaving(false);
    }
  };

  const addPrescription = () => {
    setPrescriptions([
      ...prescriptions,
      { medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' },
    ]);
  };

  const removePrescription = (index: number) => {
    setPrescriptions(prescriptions.filter((_, i) => i !== index));
  };

  const updatePrescription = (index: number, field: keyof Prescription, value: string) => {
    const updated = [...prescriptions];
    updated[index] = { ...updated[index], [field]: value };
    setPrescriptions(updated);
  };

  const renderPatientInfoStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Patient Information" />
      <Divider />
      <CardContent>
        {currentAppointment ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Patient Name</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>{currentAppointment.patientName}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Age / Gender</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {currentAppointment.patientAge || 'N/A'} / {currentAppointment.patientGender || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>{currentAppointment.patientMobile || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Appointment Time</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>{currentAppointment.appointmentTime}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Chief Complaints</Typography>
              <Typography variant="body1">{currentAppointment.chiefComplaints || currentAppointment.symptoms || 'Not specified'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Visit Address</Typography>
              <Typography variant="body1">{currentAppointment.fullVisitAddress || 'N/A'}</Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography color="text.secondary">Loading patient information...</Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderVitalsStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Vital Signs" subheader="Record patient vitals" />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Blood Pressure (Systolic)" type="number" value={vitalSigns.bloodPressureSystolic || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, bloodPressureSystolic: Number(e.target.value) || null })} placeholder="120" helperText="mmHg" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Blood Pressure (Diastolic)" type="number" value={vitalSigns.bloodPressureDiastolic || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, bloodPressureDiastolic: Number(e.target.value) || null })} placeholder="80" helperText="mmHg" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Pulse Rate" type="number" value={vitalSigns.pulseRate || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, pulseRate: Number(e.target.value) || null })} placeholder="72" helperText="bpm" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Temperature" type="number" value={vitalSigns.temperature || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, temperature: Number(e.target.value) || null })} placeholder="98.6" helperText="°F" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Respiratory Rate" type="number" value={vitalSigns.respiratoryRate || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, respiratoryRate: Number(e.target.value) || null })} placeholder="16" helperText="breaths/min" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Oxygen Saturation" type="number" value={vitalSigns.oxygenSaturation || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, oxygenSaturation: Number(e.target.value) || null })} placeholder="98" helperText="%" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Weight" type="number" value={vitalSigns.weight || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, weight: Number(e.target.value) || null })} placeholder="70" helperText="kg" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Height" type="number" value={vitalSigns.height || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, height: Number(e.target.value) || null })} placeholder="170" helperText="cm" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="BMI" value={vitalSigns.bmi || ''} disabled helperText="Auto-calculated" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Blood Sugar (Fasting)" type="number" value={vitalSigns.bloodSugarFasting || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, bloodSugarFasting: Number(e.target.value) || null })} placeholder="100" helperText="mg/dL" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Blood Sugar (Post Meal)" type="number" value={vitalSigns.bloodSugarPostMeal || ''} onChange={(e) => setVitalSigns({ ...vitalSigns, bloodSugarPostMeal: Number(e.target.value) || null })} placeholder="140" helperText="mg/dL" />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderSymptomsStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Symptoms & Clinical Findings" />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Chief Complaints" multiline rows={2} value={chiefComplaints} onChange={(e) => setChiefComplaints(e.target.value)} placeholder="Patient's main complaints..." />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Symptoms Checklist</Typography>
            <Grid container spacing={1}>
              {['fever', 'cough', 'bodyPain', 'headache', 'cold', 'breathlessness', 'chestPain', 'dizziness', 'fatigue', 'nausea'].map((symptom) => (
                <Grid item xs={6} md={3} key={symptom}>
                  <FormControlLabel control={<Switch checked={symptoms[symptom as keyof Symptoms] as boolean} onChange={(e) => setSymptoms({ ...symptoms, [symptom]: e.target.checked })} />} label={symptom.charAt(0).toUpperCase() + symptom.slice(1)} />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Other Symptoms / Notes" multiline rows={2} value={symptoms.other} onChange={(e) => setSymptoms({ ...symptoms, other: e.target.value })} placeholder="Any other symptoms..." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Past Medical History" multiline rows={2} placeholder="Previous illnesses, surgeries, hospitalizations..." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Current Medications" multiline rows={2} placeholder="List current medications..." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Allergies" multiline rows={1} placeholder="Known allergies..." />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderDiagnosisStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Diagnosis" />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Provisional Diagnosis" multiline rows={2} value={diagnosis.provisional} onChange={(e) => setDiagnosis({ ...diagnosis, provisional: e.target.value })} placeholder="Initial assessment..." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Final Diagnosis" multiline rows={2} value={diagnosis.final} onChange={(e) => setDiagnosis({ ...diagnosis, final: e.target.value })} placeholder="Confirmed diagnosis..." required />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="ICD Code" value={diagnosis.icdCode} onChange={(e) => setDiagnosis({ ...diagnosis, icdCode: e.target.value })} placeholder="e.g., J00" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Clinical Findings" multiline rows={3} value={diagnosis.clinicalFindings} onChange={(e) => setDiagnosis({ ...diagnosis, clinicalFindings: e.target.value })} placeholder="Detailed clinical examination findings..." />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderPrescriptionStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Prescription" action={<Button variant="outlined" startIcon={<HospitalIcon />} onClick={addPrescription}>Add Medicine</Button>} />
      <Divider />
      <CardContent>
        {prescriptions.map((prescription, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'transparent' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Medicine Name" value={prescription.medicineName} onChange={(e) => updatePrescription(index, 'medicineName', e.target.value)} placeholder="e.g., Paracetamol" />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Dosage" value={prescription.dosage} onChange={(e) => updatePrescription(index, 'dosage', e.target.value)} placeholder="e.g., 500mg" />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select value={prescription.frequency} onChange={(e) => updatePrescription(index, 'frequency', e.target.value)} label="Frequency">
                    <MenuItem value="1-0-0">Once daily (Morning)</MenuItem>
                    <MenuItem value="1-1-0">Twice daily</MenuItem>
                    <MenuItem value="1-1-1">Three times daily</MenuItem>
                    <MenuItem value="0-0-1">Night only</MenuItem>
                    <MenuItem value="SOS">As needed (SOS)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Duration" value={prescription.duration} onChange={(e) => updatePrescription(index, 'duration', e.target.value)} placeholder="e.g., 5 days" />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Instructions" value={prescription.instructions} onChange={(e) => updatePrescription(index, 'instructions', e.target.value)} placeholder="e.g., After meals" />
              </Grid>
              {prescriptions.length > 1 && (
                <Grid item xs={12}>
                  <Button variant="text" color="error" onClick={() => removePrescription(index)}>Remove</Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        ))}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <TextField fullWidth label="Dosage Instructions" multiline rows={2} placeholder="General dosage instructions..." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Diet Instructions" multiline rows={2} placeholder="Dietary recommendations..." />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderFollowUpStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Follow-up & Notes" />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={followUp.required} onChange={(e) => setFollowUp({ ...followUp, required: e.target.checked })} />} label="Follow-up Required" />
          </Grid>
          {followUp.required && (
            <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Follow-up Date" type="date" value={followUp.date} onChange={(e) => setFollowUp({ ...followUp, date: e.target.value })} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select value={followUp.priority} onChange={(e) => setFollowUp({ ...followUp, priority: e.target.value })} label="Priority">
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="NORMAL">Normal</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="URGENT">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Follow-up Instructions" multiline rows={2} value={followUp.instructions} onChange={(e) => setFollowUp({ ...followUp, instructions: e.target.value })} placeholder="Instructions for follow-up..." />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField fullWidth label="Additional Notes" multiline rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes..." />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return renderPatientInfoStep();
      case 1: return renderVitalsStep();
      case 2: return renderSymptomsStep();
      case 3: return renderDiagnosisStep();
      case 4: return renderPrescriptionStep();
      case 5: return renderFollowUpStep();
      default: return null;
    }
  };

  if (appointmentLoading) {
    return (<Box sx={{ p: 3 }}><LinearProgress /><Typography sx={{ mt: 2, textAlign: 'center' }}>Loading appointment details...</Typography></Box>);
  }

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Medical Examination</Typography>
          <Typography variant="body1" color="text.secondary">Complete visit report for appointment #{appointmentId}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate(-1)}>Back</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Stepper */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
        </Stepper>
      </Paper>

      {/* Step content */}
      {renderStepContent()}

      {/* Action Buttons */}
      <Paper sx={{ p: 2, position: 'sticky', bottom: 0, zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Button disabled={activeStep === 0 || saving} onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
            <Button variant="outlined" onClick={handleSaveDraft} disabled={saving}>Save Draft</Button>
          </Box>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={saving || !diagnosis.final} startIcon={saving ? <LinearProgress sx={{ width: 20 }} /> : <SubmitIcon />}>Submit Report</Button>
            ) : (
              <Button variant="contained" onClick={handleNext} disabled={saving} endIcon={<NextIcon />}>Next</Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateVisitReportPage;